export type Variables = Record<string, unknown>;

interface GraphQLResponseError {
  message?: string;
}

interface GraphQLResponseBody<TResponse> {
  data?: TResponse;
  errors?: GraphQLResponseError[];
}

export class GraphQLRequestError extends Error {
  readonly status?: number;
  readonly errors?: GraphQLResponseError[];

  constructor(message: string, status?: number, errors?: GraphQLResponseError[]) {
    super(message);
    this.name = 'GraphQLRequestError';
    this.status = status;
    this.errors = errors;
  }
}

export interface GraphQLClient {
  request<TResponse>(query: string, variables?: Variables): Promise<TResponse>;
}

/**
 * Minimal GraphQL client over native fetch.
 *
 * Replaces `graphql-request`, which pulled the whole `graphql` package (parser, printer,
 * validator) plus `cross-fetch` into the shared app chunk on every page — roughly 180 KB
 * uncompressed — even though only /find and /chat-gpt issue client-side queries.
 */
export const createGraphQLClient = (endpoint: string): GraphQLClient => ({
  async request<TResponse>(query: string, variables?: Variables): Promise<TResponse> {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!response.ok) {
      throw new GraphQLRequestError(`GraphQL request failed with status ${response.status}`, response.status);
    }

    const body: GraphQLResponseBody<TResponse> = await response.json();

    if (body.errors?.length) {
      throw new GraphQLRequestError(body.errors[0]?.message ?? 'GraphQL request returned errors', response.status, body.errors);
    }

    if (!body.data) {
      throw new GraphQLRequestError('GraphQL response contained no data', response.status);
    }

    return body.data;
  },
});
